var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { MapSchema, Schema, type } from '@colyseus/schema';
import { DEAD_ACTION, IDLE_ACTION, endWalkingAction } from './actions.js';
import { GroundItemState } from './ground.js';
import { NO_INTENT_APPLIED } from './messages.js';
import { MonsterState } from './monsters.js';
/**
 * Replicated world state — the co-op dungeon room, twenty times a second.
 *
 * These are classes with decorated fields because `@colyseus/schema` requires
 * exactly that shape to generate its binary patches — this is one of the two
 * places in the repo where a class is not a style choice. Treat them as dumb
 * wire containers: no behaviour, no rules, no gameplay. The simulation lives in
 * `@d2/core`, which never sees this package.
 *
 * ## The one decision this file encodes: a walking entity replicates its position every tick
 *
 * The alternative was to send a destination once and let every client re-run
 * `@d2/core`'s `findPath` + `stepWalker` to work out where everybody else is.
 * That is roughly fifty times cheaper on the wire and it was rejected:
 *
 *   - **`ARCHITECTURE.md` predicts one entity, not all of them.** The client
 *     "interpolat[es] remote entities behind a ~100 ms buffer and predict[s]
 *     *only its own* movement". An interpolation buffer is a queue of
 *     timestamped samples; with no samples arriving there is nothing to
 *     interpolate between. Prediction is for the entity whose input you have.
 *   - **It would put the pathfinder on the wire.** To place a remote player,
 *     every client would have to reproduce A*, string pulling and the clearance
 *     erosion bit for bit, from the exact float64 position and tick the server
 *     started that walk on. `packages/schema` may not restate a game rule — and
 *     a wire format that is only correct while `pathfinding.ts` agrees with
 *     itself across two builds has restated the lot of them by omission. A
 *     client one commit behind would draw the party in the wrong place.
 *   - **It does not generalise past a click.** Monsters follow flow fields and
 *     re-target every tick (`ARCHITECTURE.md`; `TODO.md` #53), and knockback and
 *     block-lock (#32) move an entity that has no destination at all. The
 *     position channel would have to come back for the first monster, leaving
 *     the room maintaining two replication models. That prediction has since
 *     been tested rather than assumed: `monsters.ts` re-derives the question
 *     from scratch for a body nobody predicts, and lands on the same answer —
 *     a destination for a chaser is *more* expensive than the positions it was
 *     supposed to replace, because a chaser rewrites it every tick.
 *   - **Correction needs it anyway.** Server position is truth and the client
 *     soft-corrects from it (`TODO.md` #52: "server state *is* the correction").
 *     A correction that only arrives when the server notices drift requires the
 *     server to model each client's prediction; a position stream is the
 *     correction, unconditionally.
 *
 * The cost was measured rather than guessed, and `bandwidth.test.ts` holds the
 * numbers to it: a player who did not move costs **nothing**, because a patch
 * is a delta; a full party of four walking non-stop is ~53 bytes a tick, about
 * 1.1 kB/s. That is the entire saving the cheaper scheme was buying.
 *
 * What the cheap scheme was *right* about is kept: the destination is on the
 * wire too (`walking`, `destinationX`, `destinationY`), at ten bytes per click
 * and nothing per tick. Positions cannot tell a client where a walk *ends*, and
 * that is the one thing it needs them for.
 *
 * ## What is deliberately absent
 *
 * Anything a client can work out is not state, and every field here had to
 * argue its way on:
 *
 *   - **Facing.** Two consecutive positions give a heading, and an idle entity
 *     keeps the last one it had. `state.test.ts` derives it rather than
 *     asserting a field exists.
 *   - **Speed.** One constant, `DEFAULT_MOVE_SPEED`, which both sides import
 *     from `@d2/core`. It becomes a wire question the day it becomes a stat
 *     (`TODO.md` #111).
 *   - **The route.** A route is an answer, and answers are the server's; a
 *     client that needs one re-derives it from the destination with the same
 *     core code. Shipping it would be the destination-only scheme's coupling
 *     bought at the position stream's price.
 *   - **The map.** A floor is a seed and the numbers it was expanded under —
 *     `LevelState` below — not sixteen kilobytes of tiles. This is the one place
 *     where re-deriving on the client is unambiguously right: the generator is
 *     seeded precisely so that both sides can, and `hashGrid` proves they did.
 *   - **A per-player timestamp.** `WorldState.tick` dates the whole snapshot.
 *   - **The two numbers an experience bar is drawn from.** `experienceIntoLevel`
 *     and `experienceForNextLevel` are what `apps/web/src/ui/hud-model.ts` takes,
 *     and neither is here: both are exact integer functions of the level and the
 *     total, read off core's own `EXPERIENCE_TABLE` by whichever side needs them.
 *     Sending them would be shipping an answer beside the question — the same
 *     trade the map refuses at four hundred times the size.
 *   - **Unspent skill and stat points.** The HUD wants them (`HudProgress`) and
 *     they are deliberately not on this map, because a `MapSchema` is a
 *     *broadcast*: what is here is told to all four players, and how many points
 *     somebody else has left to spend is not something any client renders. Life,
 *     mana and level are on the wire precisely because a party frame draws them
 *     for everybody. The honest home for a private counter is a message
 *     addressed to one seat, and nothing yet sends one — so `pointsLabel` has no
 *     source and this is the gap that has to be closed before a skill tree can
 *     be opened.
 *
 * A field this package does *not* declare cannot be sent by accident, which is
 * the only reason this list is worth writing down.
 *
 * ## Why a player's life is a number and a monster's is a band
 *
 * `monsters.ts` bands life into sixteen steps and states plainly that the
 * decision is about *write frequency* rather than field width: poison is real
 * damage on every fixed step, so an exact life is one write per poisoned body
 * per tick — 305 bytes a tick for a floor of sixty under one nova, against 19
 * for the band.
 *
 * Every term of that argument changes sign here, and the two that matter are
 * the population and the reader:
 *
 *   - **Four, not sixty.** The same nova over a whole party is four writes a
 *     tick, not sixty, and `bandwidth.test.ts` measures the pair: a party
 *     draining exactly costs about what a *floor* of sixty costs banded. The
 *     multiplier that made banding necessary is simply absent.
 *   - **One of these four is the local player, and their orb is a readout.**
 *     `hud-model.ts`'s `readoutOf` prints `137 / 214`; sixteen bands can say
 *     133.75 and no more. Worse, a band hides every hit smaller than 6% of the
 *     pool, and 6% of your own life is exactly the margin a player is judging
 *     when they decide to swing again. `TODO.md` #261 accepts that loss for a
 *     monster because nothing shows a monster's number; accepting it here would
 *     make the readout a lie.
 *   - **The maximum has to be exact anyway.** It is the denominator of every
 *     fill and the number beside the slash. Once it is a plain integer, banding
 *     the current buys two bytes and costs the truth.
 *
 * So a player carries `life`, `maxLife`, `mana` and `maxMana` as exact `uint16`
 * points. None of them is written on a step — a pool changes when something
 * happens to it — so the per-mover budget that `MAX_BYTES_PER_MOVER` guards is
 * untouched, and that is asserted rather than assumed.
 *
 * ## Field order is the wire format
 *
 * `@colyseus/schema` numbers fields by declaration order and sends the number,
 * not the name. Appending a field is compatible with an older client; moving or
 * removing one silently reinterprets everything after it. **Append only.**
 */
export class PlayerState extends Schema {
    constructor() {
        super(...arguments);
        /**
         * Colyseus session id.
         *
         * Redundant with the key this player is filed under in `WorldState.players` —
         * see `TODO.md` #113. It is set once and never changes, so it costs seven
         * bytes at join and nothing per tick, which is why removing it is a tidiness
         * question rather than a bandwidth one.
         */
        this.id = '';
        /**
         * Display name, for the nameplate.
         *
         * On the wire because a client knows session ids and nothing else — there is
         * no other channel that could tell it who it is looking at. Written once at
         * join, so it too costs nothing per tick.
         */
        this.name = '';
        /**
         * Continuous position in **cell coordinates**, like the whole of core. Never
         * pixels: the renderer owns pixels-per-cell (`TODO.md` #42, #63).
         *
         * `float32`, deliberately. The simulation runs in float64 and this halves the
         * per-tick cost of the one field that is written every tick. The replicated
         * position is therefore `Math.fround` of the truth — about 1e-6 of a cell out
         * at dungeon scale, which is far below a pixel and must stay far below the
         * soft-correction threshold it feeds (`TODO.md` #112). It is a *rendering*
         * input: a client predicts its own movement from its own float64 walker and
         * only ever corrects from this.
         */
        this.x = 0;
        this.y = 0;
        /**
         * Whether this entity is part-way along a walk.
         *
         * Not derivable in time to be useful. A client can compare two consecutive
         * positions, but that is a tick late, it cannot distinguish "stopped" from "no
         * patch has arrived yet", and a client that has just joined has exactly one
         * sample and would draw a walking party standing still. One byte, written
         * twice per walk, buys the idle/walk animation split and tells the
         * interpolator whether the stream ended or the entity did.
         */
        this.walking = false;
        /**
         * The **cell** this walk ends on, meaningful only while `walking`.
         *
         * Two reasons it is worth its ten bytes per click:
         *
         *   - It bounds interpolation. Without a stop, a client rendering behind the
         *     buffer extrapolates a remote player straight past the point it halted
         *     on and then snaps it back — the classic visible artefact.
         *   - It is the server's acknowledgement of a `moveTo` intent. The clicked
         *     cell and the accepted one routinely differ: the server validates every
         *     intent (`ARCHITECTURE.md`, anti-cheat) and, since `TODO.md` #92, an
         *     unreachable click walks to the *nearest reachable* cell instead. A
         *     client that never hears which destination was accepted keeps predicting
         *     a walk that is not happening, and finds out only through drift. What it
         *     still cannot do is tell two clicks apart — `TODO.md` #110.
         *
         * `int16` because a destination is a whole cell index, so the type both
         * compresses it and states that. The caller supplies a cell; resolving a click
         * to one is core's rule, not this package's. Signed, so a coordinate system
         * that ever admits a negative origin does not wrap silently.
         *
         * Left stale on arrival rather than zeroed — see `clearDestination`.
         */
        this.destinationX = 0;
        this.destinationY = 0;
        /**
         * The sequence number of the last `moveTo` the server **applied** to this
         * player, or `NO_INTENT_APPLIED` when it has applied none (`TODO.md` #110).
         *
         * The identity the destination cannot supply. `destinationX/Y` says *where*
         * the server is sending this entity; this says *which click* it was answering
         * — and the two are needed together, because walk-as-close-as-you-can means
         * the accepted cell is routinely not the clicked one.
         *
         * Three consequences worth stating, because each is load-bearing:
         *
         *   - **"Applied", not "received".** An intent the server refused is never
         *     written here, so a client whose sequence has not appeared knows its
         *     click did not happen. That is the whole rejection channel, and it costs
         *     no message of its own.
         *   - **It outlives the walk.** `clearDestination` ends a walk and leaves this
         *     alone: the last click the server acted on is still the last click it
         *     acted on after the entity arrives.
         *   - **It is written per click, never per tick**, so it costs nothing in the
         *     stream `bandwidth.test.ts` guards.
         *
         * `uint32` because a click counter only ever goes up and a session cannot
         * plausibly reach four billion clicks — the wrap that a `uint16` would hit
         * after a long evening would break the ordering the field exists to carry.
         */
        this.lastSequence = NO_INTENT_APPLIED;
        /**
         * Whether this seat's character is dead and waiting to come back — appended,
         * because field order is the wire format.
         *
         * **Not derivable, which is the whole of the argument.** A dead player's body
         * leaves the simulation (`apps/game`'s tick order), so nothing writes its
         * position any more and `walking` is false: on the wire that is *identical*
         * to a player standing perfectly still, and a client cannot tell a corpse
         * from somebody reading their inventory. Everything else in this file that
         * had to argue its way on made the same argument — `walking` exists because
         * "stopped" and "no patch has arrived yet" look alike — and this is the
         * sharper case of it, because the two states differ in what the *player* can
         * do about them.
         *
         * One byte, written twice per death: once when the pool empties, once when
         * the character stands up. It costs nothing per tick.
         *
         * It is a fact about the **seat**, not about a body. That is why a dead
         * player still has an entry in `WorldState.players` at all: the connection is
         * open, the party is still fighting, and the character is coming back.
         */
        this.dead = false;
        /**
         * The tick this character comes back on. Meaningful only while `dead`.
         *
         * A death is a *countdown*, and a countdown is most of what a death screen
         * is. Nothing else on the wire could produce one: a client that watched the
         * death still does not know how long the wait is (the delay is core's, but
         * the tick it started on is the server's), and a client that **joined after
         * it** has no patch history to infer anything from at all — which is exactly
         * the case a discrete "somebody died" message could not serve, and the reason
         * this is state rather than a message.
         *
         * Written once per death, with the flag, so it costs nothing per tick.
         * `WorldState.tick` dates the snapshot it is compared against, and core's
         * `TICK_MS` turns the difference into seconds, so no second clock crosses.
         *
         * `uint32`, the same width and the same seven-year wrap as the tick counter
         * it is measured against — a respawn tick that could not hold what
         * `WorldState.tick` holds would count down to the wrong moment. Left stale
         * when the character returns rather than zeroed, exactly as the destination
         * is: `dead === false` already tells every client to ignore it, and clearing
         * it would be a second field change on the wire for nothing.
         */
        this.respawnTick = 0;
        /**
         * Life, in whole points — appended, because field order is the wire format.
         *
         * **Exact, not banded.** The argument is in the module header and the numbers
         * are in `bandwidth.test.ts`; the short version is that a band is a
         * write-frequency saving worth 286 bytes a tick at sixty bodies and about
         * nineteen at four, and that one of these four is reading their own number off
         * an orb.
         *
         * `uint16`, which is two decisions:
         *
         *   - **Whole points, so the field is an integer at all.** Core carries life
         *     as a plain `number` and poison lands in instalments that need not be
         *     whole, so somewhere it has to be rounded; `setPlayerLife` is that place
         *     and it rounds *up*, for the reason `setMonsterLife` does.
         *   - **65 535, which is 29 times the largest pool this game can currently
         *     produce.** `deriveLife` is `base + perAttributePoint × vitality +
         *     perLevel × (level − 1)` and every one of those three numbers is
         *     injected, because the per-class life tables are a balance decision
         *     nobody has made (`TODO.md` #22, #30). D2's own ceiling is a few
         *     thousand. A `uint8` could not hold a level-20 character and a `uint32`
         *     would spend two bytes a write on headroom nothing will use — and a
         *     maximum the field *would* wrap is refused loudly rather than encoded
         *     into some smaller pool.
         *
         * Zero on both is "nobody has described this seat". It is not a state a
         * character can be in — `createVitals` refuses a pool of nothing — so an empty
         * orb reads as an omission rather than as a plausible lie, and it is the
         * encoder's cheapest value into the bargain.
         */
        this.life = 0;
        this.maxLife = 0;
        /**
         * Mana, in whole points. `uint16` for the same two reasons life is, and core
         * already holds it whole: `mana.ts` moves points and keeps its 256ths in a
         * `fraction` that never crosses this wire, because regeneration in progress is
         * a rule's private state and not something anybody draws.
         *
         * The write frequency is the *lower* of the two and it is worth stating,
         * because a regenerating pool sounds like a per-tick field. It is not:
         * `manaRegenPerTick` earns 256ths and `stepMana` only ever hands back a
         * different pool when a whole point lands. At core's `MANA_REGEN_SECONDS` that
         * is one write per one and a quarter seconds for a hundred-point pool — about
         * one twenty-fifth of the rate a position is written at, per player.
         */
        this.mana = 0;
        this.maxMana = 0;
        /**
         * Character level.
         *
         * `uint8`. Core's `MAX_CHARACTER_LEVEL` is D2's own 99 and the field holds
         * 255, so the cap can move a long way before the wire has to — and a test
         * derives that comparison from core rather than restating 99, because
         * `TODO.md` #450 has the curve itself under review.
         *
         * It is not derivable from `experience`, even though `levelForExperience`
         * would answer it: that would make every client's level a function of every
         * client's copy of the table, and a client one release behind would draw the
         * party at the wrong level for the same reason a client that re-ran the
         * pathfinder would draw it in the wrong place. One byte, written once per
         * level-up.
         *
         * Zero is reserved for "nobody wrote this", exactly as `NO_ARCHETYPE` is.
         * Levels count from one, so defaulting to one would make a seat the room
         * forgot to describe look like a fresh character rather than like a bug.
         */
        this.level = 0;
        /**
         * Lifetime experience — the **absolute total**, not progress inside the level.
         *
         * `uint32`, and the ceiling is the reason to say which of the two it is.
         * Standing at level 99 costs 3 520 485 254 (core's `EXPERIENCE_TABLE`, D2's
         * `Experience.txt` row for row). The database column is a `BIGINT`, which
         * invites the conclusion that no 32-bit field can hold it — but MySQL's `INT`
         * is *signed* and stops at 2 147 483 647, while an unsigned 32-bit wire field
         * stops at 4 294 967 295 and carries the whole curve with 18% to spare. Those
         * are different ceilings and only one of them was ever crossed.
         *
         * **The total rather than the progress**, because the total is the thing a
         * client cannot rebuild and the progress is the thing it can. `hud-model.ts`
         * takes `experienceIntoLevel` and `experienceForNextLevel`; both fall out of
         * this field and `level` through core's own `experienceForLevel` and
         * `experienceToNextLevel` — exact integer arithmetic over a 99-row table both
         * sides already ship. Sending the progress instead would put a *derived* pair
         * on the wire, cost a second field, and still not let a character sheet show
         * a lifetime total.
         *
         * Written once per kill and never per tick, and `gainExperience` banks nothing
         * at the ceiling — so a level-99 character costs this field no delta at all.
         */
        this.experience = 0;
        /**
         * What this body is **doing** — appended, because field order is the wire
         * format.
         *
         * The whole argument is in `actions.ts`, which owns the vocabulary and both
         * agreements; the two sentences that belong here are why it is on a seat at
         * all and why it is not three more booleans.
         *
         * **Not derivable, and not even nearly.** `walking` and `dead` were the entire
         * animation vocabulary of this wire, so a character winding a swing at
         * something, taking a blow, blocking one or mid-cast was replicated as a
         * character standing perfectly still — the same indistinguishability `dead`
         * itself was added to fix, on the states a fight is actually made of. Nothing
         * a client holds could tell them apart: the life pool moves for a blow but not
         * for a block or a miss, and it moves for poison as well, which is not a blow
         * at all.
         *
         * **One `uint8` rather than a flag per state**, because a body is doing one
         * thing: four booleans is four field indices on every body and four ways for
         * two clients to disagree about which of "hurt" and "striking" wins. One field
         * carries the answer the server already has.
         *
         * `uint8` for the reason `archetype` is one — the field costs two bytes at any
         * width and the spare codes are what let an eighth state be appended without a
         * wire break — and `IDLE_ACTION` is zero because idle is what the two booleans
         * beside it already default to, so a seat costs nothing to say so.
         *
         * Written every tick and paid for on transitions only: a value equal to the
         * one the field already holds is not a change (`bandwidth.test.ts`), so a
         * character striking for the whole of a swing costs two bytes for the swing
         * rather than two a tick.
         */
        this.action = IDLE_ACTION;
    }
}
__decorate([
    type('string'),
    __metadata("design:type", Object)
], PlayerState.prototype, "id", void 0);
__decorate([
    type('string'),
    __metadata("design:type", Object)
], PlayerState.prototype, "name", void 0);
__decorate([
    type('float32'),
    __metadata("design:type", Object)
], PlayerState.prototype, "x", void 0);
__decorate([
    type('float32'),
    __metadata("design:type", Object)
], PlayerState.prototype, "y", void 0);
__decorate([
    type('boolean'),
    __metadata("design:type", Object)
], PlayerState.prototype, "walking", void 0);
__decorate([
    type('int16'),
    __metadata("design:type", Object)
], PlayerState.prototype, "destinationX", void 0);
__decorate([
    type('int16'),
    __metadata("design:type", Object)
], PlayerState.prototype, "destinationY", void 0);
__decorate([
    type('uint32'),
    __metadata("design:type", Object)
], PlayerState.prototype, "lastSequence", void 0);
__decorate([
    type('boolean'),
    __metadata("design:type", Object)
], PlayerState.prototype, "dead", void 0);
__decorate([
    type('uint32'),
    __metadata("design:type", Object)
], PlayerState.prototype, "respawnTick", void 0);
__decorate([
    type('uint16'),
    __metadata("design:type", Object)
], PlayerState.prototype, "life", void 0);
__decorate([
    type('uint16'),
    __metadata("design:type", Object)
], PlayerState.prototype, "maxLife", void 0);
__decorate([
    type('uint16'),
    __metadata("design:type", Object)
], PlayerState.prototype, "mana", void 0);
__decorate([
    type('uint16'),
    __metadata("design:type", Object)
], PlayerState.prototype, "maxMana", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], PlayerState.prototype, "level", void 0);
__decorate([
    type('uint32'),
    __metadata("design:type", Object)
], PlayerState.prototype, "experience", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], PlayerState.prototype, "action", void 0);
/**
 * The floor everybody in this room is standing on, in about forty bytes.
 *
 * `ARCHITECTURE.md`: *"Levels cost ~40 bytes on the wire. The server picks a
 * seed and sends `{seed, depth, biome, params}`; the client regenerates a
 * byte-identical cave. `hashGrid` lets both sides checksum instead of shipping
 * tiles."* This is that message. A 128×128 grid of tiles is sixteen kilobytes
 * and it would have to arrive before a client could draw anything at all; the
 * seed and the numbers it was expanded under are the same map at four hundredths
 * of a percent of the size, and they are the reason the generator is seeded in
 * the first place.
 *
 * The ratio is not fixed, and that is the point: this descriptor costs the same
 * forty-one bytes at any size, so the tiles it replaces grow while it does not.
 * The house floor quadrupling on 2026-08-12 cost this wire nothing at all,
 * which is what let the size be decided on how the game *plays* (`TODO.md`
 * #350) rather than on what a client could afford to download.
 *
 * Written once, in `onCreate`, before any client exists. It therefore rides in
 * the snapshot every joining client is handed — including one that joins an
 * hour in — and never appears in a patch again. `bandwidth.test.ts` holds it to
 * that: a level that is re-sent per tick would cost more per second than every
 * position in a full party.
 *
 * ## These numbers have to be exact, not close
 *
 * A regenerated cave is either identical or it is a different game. Two fields
 * are load-bearing about that and are typed for it rather than for their size:
 *
 *   - **`seed` is `number`**, `@colyseus/schema`'s variable-length encoding, not
 *     an `int32`. A room with no seed offered to it seeds from the clock, which
 *     is around 1.8e12 and would wrap in any 32-bit field — silently, into some
 *     other cave. The encoding costs one byte for a small seed and nine for a
 *     clock reading, and is exact for every safe integer.
 *   - **`wallChance` is `float64`.** The generator compares an rng draw against
 *     it per cell; rounded to float32 it is 0.44999998807907104, and a draw
 *     landing in that sliver flips one cell of the cave on one side only.
 *
 * ## Who checks, and why it is not this side
 *
 * `gridHash` is the server's claim about the floor it is simulating. Verifying
 * it belongs to the *receiver*: the client is the only side holding a second,
 * independently grown grid, and a server comparing its hash with its own grid
 * compares a thing with itself. A mismatch means the two sides are playing
 * different maps and every position, path and wall after it is a lie, so the
 * client must refuse the room loudly rather than render a world that will
 * disagree with the server about where the rock is (`TODO.md` #170).
 *
 * The check itself does not live in this package for the reason nothing else
 * does: `@d2/schema` imports `@d2/core` for types only, so no simulation is
 * pulled into a client bundle by the wire format, and regenerating a dungeon is
 * not describing a wire. `dungeonParams` is the part that *is* this package's:
 * it hands the caller core's own options object, so the client cannot map the
 * fields wrongly and cannot forget one the day core grows another.
 */
export class LevelState extends Schema {
    constructor() {
        super(...arguments);
        /** The whole cave in one number. The same seed regrows it anywhere, on any build. */
        this.seed = 0;
        /**
         * Which floor of the descent this is, counting from one.
         *
         * `uint8`, so a descent may be 255 floors deep — D2's game is forty-ish areas
         * and this is a floor counter, not a level cap.
         */
        this.depth = 1;
        /** The generator's parameters, exactly as the floor was grown under them. */
        this.width = 0;
        this.height = 0;
        this.wallChance = 0;
        this.smoothingPasses = 0;
        /**
         * `hashGrid` of the floor the server is simulating.
         *
         * `uint32` because that is what `hashGrid` returns — an FNV word, unsigned.
         * A signed field would wrap half of every hash into a negative number and the
         * comparison would fail on caves that are in fact identical.
         */
        this.gridHash = 0;
        /**
         * The **cell** this floor's stairs down are on — appended, because field
         * order is the wire format.
         *
         * ## It is here for the same reason `gridHash` is, and the hash cannot cover it
         *
         * Six bytes, once per floor, and they buy the one disagreement the checksum
         * beside them is blind to. `placeStairs` is a function of the **body** it is
         * solved for — a cell too tight for a mover is never expanded, so the farthest
         * *reachable* cell depends on the radius — and no radius exists in `@d2/core`
         * at all: `PLAYER_RADIUS` is `apps/game`'s (`TODO.md` #71). A client that
         * regrew the floor and placed its own stairs for any other body would put the
         * exit somewhere else **on a grid that hashes identically**, because the grid
         * *is* identical. The hash would pass, the seed would match, and the two sides
         * would simply disagree about where the way down is.
         *
         * So the server publishes the cell it is actually simulating and the client
         * checks its own against it — the same shape `gridHash` already has, and for
         * the same reason: the receiver is the only side holding a second, independent
         * answer, and a server comparing its cell with its own would compare a thing
         * with itself (`TODO.md` #170).
         *
         * ## Not derivable, which is what earns it the six bytes
         *
         * Everything else on this descriptor is an *input* a client re-runs the
         * generator on. This is an *output* of a rule the client cannot run without a
         * number the server never sent it. Sending the radius instead would be
         * cheaper by two bytes and worse in every other way: it is a fact about a
         * *body* rather than about a floor, it would put a full-map Dijkstra sweep on
         * every client's join path to recover a cell the server already has, and a
         * client one release behind would still disagree the day the sweep changes.
         *
         * `int16`, the same width and the same reasoning as `destinationX/Y`: a cell
         * is a whole number, the type says so, and signed so a coordinate system that
         * ever admits a negative origin does not wrap silently.
         */
        this.stairsX = 0;
        this.stairsY = 0;
    }
}
__decorate([
    type('number'),
    __metadata("design:type", Object)
], LevelState.prototype, "seed", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], LevelState.prototype, "depth", void 0);
__decorate([
    type('uint16'),
    __metadata("design:type", Object)
], LevelState.prototype, "width", void 0);
__decorate([
    type('uint16'),
    __metadata("design:type", Object)
], LevelState.prototype, "height", void 0);
__decorate([
    type('float64'),
    __metadata("design:type", Object)
], LevelState.prototype, "wallChance", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], LevelState.prototype, "smoothingPasses", void 0);
__decorate([
    type('uint32'),
    __metadata("design:type", Object)
], LevelState.prototype, "gridHash", void 0);
__decorate([
    type('int16'),
    __metadata("design:type", Object)
], LevelState.prototype, "stairsX", void 0);
__decorate([
    type('int16'),
    __metadata("design:type", Object)
], LevelState.prototype, "stairsY", void 0);
export class WorldState extends Schema {
    constructor() {
        super(...arguments);
        /**
         * Server tick this snapshot was produced on. Authoritative; the client never
         * sets it.
         *
         * The timestamp on every sample in an interpolation buffer, and the reason no
         * player carries a clock field of its own: one counter dates the whole
         * snapshot. Ticks become milliseconds through core's `TICK_MS`, so the ~100 ms
         * interpolation delay is a whole number of samples back.
         *
         * `uint32` at 20 Hz wraps after about seven years of uninterrupted uptime.
         */
        this.tick = 0;
        /**
         * Keyed by Colyseus session id.
         *
         * A map rather than an array because players join and leave in any order, and
         * a `MapSchema` patch names the entry that changed instead of re-indexing the
         * ones that did not.
         */
        this.players = new MapSchema();
        /**
         * The floor this room is on — appended, because field order is the wire
         * format and everything before it is already deployed.
         *
         * A child schema rather than seven fields on `WorldState`, so "the level" is
         * one thing a client can hand to `generateDungeon` and one thing this package
         * can charge for. It exists from construction and is filled in once; an
         * all-zero level is a room that has not made its floor yet, which is a state
         * no client ever sees because `onCreate` runs before anybody can join.
         */
        this.level = new LevelState();
        /**
         * Everything on the floor that is not a player, keyed by the id the room
         * spawned it under — appended, because field order is the wire format.
         *
         * A second map rather than one map of entities: a player and a monster share
         * almost no fields (a monster has no destination, no sequence, no name; a
         * player has no life band, no archetype, no affixes), and a union of the two
         * would put every absent field on the wire as a default for every body on the
         * floor. Two maps also let `bandwidth.test.ts` charge them separately, which
         * is the only way the population's cost is visible at all.
         *
         * The key is the id. `PlayerState` carries a second copy of its own
         * (`TODO.md` #113); at sixty bodies that is 258 bytes of every join, so
         * `MonsterState` does not — and the room owns the ids, which is where the
         * question of reusing one belongs (`TODO.md` #201).
         *
         * Empty from construction and filled by the server at runtime — that is the
         * decision this map exists to serve. It costs two bytes in the snapshot of a
         * room that has spawned nothing.
         */
        this.monsters = new MapSchema();
        /**
         * What is lying on the floor, keyed by the id the room minted for it —
         * appended, because field order is the wire format.
         *
         * A third map, for the same reason there are two already: a dropped item
         * shares no field at all with a body. It has no life, no archetype, no
         * destination and no sequence; it has a tier and a base, which nothing else
         * on this wire has. Folding it into either map would put every absent field
         * on the wire as a default for every entry of both.
         *
         * It is also the one map whose entries **never change**. A body is written
         * every tick it moves; a drop is written once and deleted once, so a floor
         * strewn with loot costs nothing at all in the per-tick stream and is charged
         * only to the client that joins after the fight. `bandwidth.test.ts` measures
         * both halves of that.
         *
         * Empty from construction: a room that has killed nothing has nothing on its
         * floor, and that costs two bytes in its snapshot.
         */
        this.ground = new MapSchema();
    }
}
__decorate([
    type('uint32'),
    __metadata("design:type", Object)
], WorldState.prototype, "tick", void 0);
__decorate([
    type({ map: PlayerState }),
    __metadata("design:type", Object)
], WorldState.prototype, "players", void 0);
__decorate([
    type(LevelState),
    __metadata("design:type", Object)
], WorldState.prototype, "level", void 0);
__decorate([
    type({ map: MonsterState }),
    __metadata("design:type", Object)
], WorldState.prototype, "monsters", void 0);
__decorate([
    type({ map: GroundItemState }),
    __metadata("design:type", Object)
], WorldState.prototype, "ground", void 0);
/**
 * The deepest floor this wire can describe.
 *
 * `LevelState.depth` is a `uint8`, so 255 — and unlike every other ceiling in
 * this file that is a *reachable* one now that a descent exists: `descend`
 * states no maximum on purpose (a ceiling would silently cap the item level of
 * the whole game), so the only thing standing between a long run and a floor 256
 * that arrives as floor 0 is this number.
 */
export const MAX_WIRE_DEPTH = 0xff;
/**
 * A depth this wire can carry.
 *
 * Exported because two places have to agree about it and only one of them can
 * afford to find out the hard way: `describeLevel` throws, which is right for a
 * server holding a floor it cannot describe, and would be a room falling over
 * mid-descent — so `apps/game` asks this *before* it rebuilds a floor and
 * refuses the descent instead. One predicate, two callers, no second copy of
 * the number (`isIntentSequence` is exported for the same reason).
 *
 * Below one is refused with the wrap: depth counts from one, so zero is the
 * value an undescribed level would carry and never a floor anybody is on.
 */
export const isWireDepth = (depth) => Number.isInteger(depth) && depth >= 1 && depth <= MAX_WIRE_DEPTH;
/**
 * Describes the floor a room is on, once per floor.
 *
 * Every field is written together for the same reason `setDestination` writes
 * three: they describe one fact, and a level whose seed and hash came from
 * different floors would send a client off to grow a cave the server is not
 * simulating — the exact failure the hash exists to catch, caused by the code
 * that publishes the hash. One writer, one call, no half-described floor. The
 * stairs joined that set rather than getting a writer of their own precisely
 * because a stairs cell from one floor beside a seed from another is the same
 * failure wearing different clothes.
 *
 * Called from `onCreate` and from the descent that replaces the floor
 * (`apps/game`'s `DescentRoom`) — **once per floor and never per tick**, which
 * `bandwidth.test.ts` measures: a level re-sent every step would cost more per
 * second than the entire party's movement.
 *
 * A depth this wire cannot carry is malformed construction rather than an
 * ordinary outcome — a `uint8` would wrap floor 256 into floor 0, and every
 * client would draw a party standing on a floor that is not a floor while the
 * server rolled loot at its real item level — so it throws, the same line
 * `markDead` and `setPlayerProgress` draw. A caller that might reach it asks
 * `isWireDepth` first.
 */
export const describeLevel = (level, { seed, depth, params, gridHash, stairs }) => {
    if (!isWireDepth(depth)) {
        throw new RangeError(`a floor depth of ${depth} is not one this wire can carry`);
    }
    level.seed = seed;
    level.depth = depth;
    level.width = params.width;
    level.height = params.height;
    level.wallChance = params.wallChance;
    level.smoothingPasses = params.smoothingPasses;
    level.gridHash = gridHash;
    level.stairsX = stairs.x;
    level.stairsY = stairs.y;
};
/**
 * The cell the stairs are on, as the pair of numbers core names a cell with.
 *
 * The client's half of the contract, exactly as `dungeonParams` is: two fields
 * become one `Vec2` in one place, so nothing downstream has to remember which
 * of them is which — and the comparison a client owes this descriptor
 * (`placeStairs(grid, entrance, ownBody)` against this) is written against the
 * same shape core hands back.
 */
export const levelStairs = (level) => ({ x: level.stairsX, y: level.stairsY });
/**
 * The replicated parameters, as the options object `generateDungeon` takes.
 *
 * The client's half of the contract, in one place: `generateDungeon(createRng(
 * level.seed), dungeonParams(level))` is the whole regeneration, and nothing
 * downstream has to remember which wire field feeds which option.
 *
 * `DungeonOptions` has no optional members, which is the point of returning it
 * rather than a shape of this package's own: the day core grows an option, this
 * function stops compiling and the wire has to grow with it — instead of both
 * sides silently falling back on a default that one of them has changed.
 */
export const dungeonParams = (level) => ({
    width: level.width,
    height: level.height,
    wallChance: level.wallChance,
    smoothingPasses: level.smoothingPasses,
});
/** The largest pool a `uint16` field can carry. A maximum past it would wrap into a smaller one. */
const MAX_POOL = 0xffff;
/** The highest level a `uint8` field can carry. Core's own cap is 99 — this is the field's, not the game's. */
const MAX_WIRE_LEVEL = 0xff;
/** The largest total a `uint32` field can carry. Level 99 costs 3 520 485 254, so this holds the curve. */
const MAX_EXPERIENCE = 0xffff_ffff;
/**
 * A pool this wire can carry, rounded the one way it may be rounded.
 *
 * Both ends round **up**, which is two rules that have to agree:
 *
 *   - *Any life at all is at least one point*, so nothing alive ever reads as a
 *     corpse. That is `setMonsterLife`'s rule for a band, and it matters more
 *     here, because a player watching their own orb hit zero is watching a
 *     death screen that is not coming.
 *   - *The maximum rounds the same way*, so a full pool stays full. Rounding the
 *     current up and the maximum down is how 214.4/214.4 becomes 215/214 — a
 *     fill outside its own orb, which every `fillOf` downstream then has to
 *     clamp away.
 *
 * A pool this wire cannot describe is malformed construction rather than an
 * ordinary outcome, so it throws — the same line `setMonsterLife` and `markDead`
 * draw. It is the server's own number and the server is holding it wrongly, and
 * the alternative is a `uint16` silently wrapping a 70 000-point pool into 4464.
 */
const banded = (pool, what) => {
    const { current, maximum } = pool;
    if (!(maximum > 0) || !(current >= 0) || !(current <= maximum) || Math.ceil(maximum) > MAX_POOL) {
        throw new RangeError(`a ${what} of ${current}/${maximum} is not one this wire can carry`);
    }
    return { current: Math.ceil(current), maximum: Math.ceil(maximum) };
};
/**
 * Records how much life a character has left.
 *
 * The single writer of `life` and `maxLife`, and it writes both together for
 * the reason `setDestination` writes three fields: they are one fact, and a
 * current left standing beside a maximum from before a level-up is an orb that
 * is wrong in a way no test of a single field would catch.
 */
export const setPlayerLife = (player, pool) => {
    const { current, maximum } = banded(pool, 'life');
    player.life = current;
    player.maxLife = maximum;
};
/** Records how much mana a character has left. The same fact, the same rounding, the other pool. */
export const setPlayerMana = (player, pool) => {
    const { current, maximum } = banded(pool, 'mana');
    player.mana = current;
    player.maxMana = maximum;
};
/**
 * Records the level and the experience total, together.
 *
 * One writer for the same reason again, and here the reason is core's own: a
 * `CharacterProgress` holds the level and the total as one fact and
 * `createCharacterProgress` **throws** when they disagree. Two writers on this
 * side would make disagreement a state the wire could be in, and a client
 * rebuilding a progress out of it would be handed a character core refuses to
 * construct.
 *
 * What is *not* checked is whether the total belongs to the level, and whether
 * the level is one a character may reach. Both are rules, rules are core's, and
 * a second copy of D2's cap living in a wire format is exactly the coupling this
 * package keeps out — `createMonsterState` declines the same invitation about
 * which affix combinations are legal. What this refuses is only what the fields
 * would wrap.
 */
export const setPlayerProgress = (player, { level, experience }) => {
    if (!(Number.isInteger(level) && level >= 1 && level <= MAX_WIRE_LEVEL)) {
        throw new RangeError(`a level of ${level} is not one this wire can carry`);
    }
    if (!(Number.isInteger(experience) && experience >= 0 && experience <= MAX_EXPERIENCE)) {
        throw new RangeError(`an experience total of ${experience} is not one this wire can carry`);
    }
    player.level = level;
    player.experience = experience;
};
/**
 * Builds a `PlayerState`. Construction goes through a function so the rest of
 * the codebase never depends on the class's field-assignment order, and so
 * every replicated player is fully populated the moment it enters the map.
 *
 * The pools and the progress go in through the same three writers a running
 * room uses, so a character the wire cannot describe is refused at the moment
 * it is seated rather than at the first patch after it.
 */
export const createPlayerState = ({ id, name, position, life, mana, progress }) => {
    const player = new PlayerState();
    player.id = id;
    player.name = name;
    player.x = position?.x ?? 0;
    player.y = position?.y ?? 0;
    if (life !== undefined)
        setPlayerLife(player, life);
    if (mana !== undefined)
        setPlayerMana(player, mana);
    if (progress !== undefined)
        setPlayerProgress(player, progress);
    return player;
};
/**
 * Starts a walk towards `cell`, in cell coordinates.
 *
 * The flag and the two coordinates are written together, in one place, for the
 * same reason `createPlayerState` exists: three fields that describe one fact
 * can be left disagreeing, and a `walking` entity pointed at a destination it
 * was given two clicks ago is a bug no test of a single field would catch.
 *
 * `cell` must be a whole cell. Resolving a click to one is core's rule and this
 * package does not restate it; the `int16` fields are what the mistake shows up
 * as if a caller ignores that.
 */
export const setDestination = (player, cell) => {
    player.destinationX = cell.x;
    player.destinationY = cell.y;
    player.walking = true;
};
/**
 * Ends a walk.
 *
 * Clears the flag and deliberately leaves the destination where it was. Zeroing
 * it would put two more field changes on the wire to overwrite a value that
 * `walking === false` already tells every client to ignore — a third of the
 * cost of a click, spent on nothing. `bandwidth.test.ts` pins that.
 *
 * The **walking action** ends with it, and only that one. `action` is the visual
 * reading of this same fact, and the agreement `actions.ts` states — a body is
 * described as walking only while it is walking — has to survive the two callers
 * that end a walk without writing an action afterwards: a descent reseats every
 * seat mid-message, and a patch can be encoded before the next tick describes
 * them again. A body that was striking is still striking when its feet stop.
 */
export const clearDestination = (player) => {
    player.walking = false;
    endWalkingAction(player);
};
/** The largest tick a `uint32` field can carry — the same ceiling `WorldState.tick` has. */
const MAX_TICK = 0xffff_ffff;
/**
 * Puts a seat on the wire as dead, and says when it comes back.
 *
 * Three fields, one fact, written together for the reason `setDestination`
 * writes three: this character has no body in the world, so it is **not
 * walking**, it **is dead**, and it returns on a **known tick**. A seat left
 * `walking` while its body is gone would be interpolated onwards towards a
 * destination nothing is moving to.
 *
 * The position is deliberately *not* touched. It is the last place the
 * character stood, which is where a corpse belongs and where a client draws
 * one; a room that zeroed it would move the body to the origin on death.
 *
 * A respawn tick this wire cannot carry is malformed construction rather than
 * an ordinary outcome — `uint32` would wrap a negative or fractional tick into
 * some other moment and the client would count down to it — so it throws, the
 * same line `setMonsterLife` draws around a life it cannot band.
 */
export const markDead = (player, respawnTick) => {
    if (!(Number.isInteger(respawnTick) && respawnTick >= 0 && respawnTick <= MAX_TICK)) {
        throw new RangeError(`a respawn tick of ${respawnTick} is not one this wire can carry`);
    }
    clearDestination(player);
    player.dead = true;
    player.respawnTick = respawnTick;
    // The fourth field of the same fact, and the **only** writer of the dead
    // action anywhere: `setPlayerAction` refuses the code and refuses every other
    // action while the flag stands, so "dead" and "doing something" cannot both be
    // on the wire about one seat. That is what makes `action === 'dead'` if and
    // only if `dead` a property of these two functions rather than a rule a room
    // has to remember (`actions.ts`).
    player.action = DEAD_ACTION;
};
/**
 * Brings a seat back to life on the wire.
 *
 * The flag alone, leaving `respawnTick` where it was, for the reason
 * `clearDestination` leaves the destination: `dead === false` already tells
 * every client to ignore it, and zeroing it would be another field change on
 * the wire buying nothing.
 *
 * It does not write the position either — the caller has the body the character
 * came back in and writes that, exactly as it does for any other move. Which
 * *cell* a character returns to is a game rule and lives in `@d2/core`; this
 * package describes what crosses.
 */
export const markAlive = (player) => {
    player.dead = false;
    // …and the reading of it, back to what a body that has just stood up is
    // doing: nothing yet. The caller describes it properly in the same tick, and
    // a value written twice before a patch is encoded costs what one write costs
    // — but a seat whose caller forgot would otherwise stand on the wire as a
    // corpse for ever, which is the failure the agreement exists to make
    // impossible in *both* directions.
    player.action = IDLE_ACTION;
};
/**
 * Puts a seat somewhere it did not walk to.
 *
 * The position and the walk, together, for the reason `setDestination` writes
 * three fields: they are one fact. Every other move on this wire is a step of a
 * walk the client is interpolating towards a destination it was told about; this
 * is the floor itself being replaced underneath the party (`apps/game`'s
 * descent), so the body is somewhere it never walked and there is no destination
 * to interpolate towards. A seat left `walking` after one would be drawn sliding
 * across a cave it is not in.
 *
 * Three things are deliberately **not** touched:
 *
 *   - **The acknowledgement.** A party descends together, so three of four
 *     seats move without having sent anything; the one that asked has its own
 *     sequence acknowledged by the room that accepted the intent.
 *   - **The death.** A seat with no body still has a countdown running and
 *     still has a corpse drawn for it. What moves is where that corpse is —
 *     to the entrance of the floor its character will come back on.
 *   - **The stale destination cell**, for the reason `clearDestination` leaves
 *     it: `walking === false` already tells every client to ignore it.
 *
 * The position is continuous, in cell coordinates, like every other position on
 * this wire — a cell centre is core's `cellCentre`, and resolving one is core's
 * rule rather than this package's.
 */
export const reseatPlayer = (player, position) => {
    clearDestination(player);
    player.x = position.x;
    player.y = position.y;
};
/**
 * Records the sequence number of the intent the server just acted on.
 *
 * Separate from `setDestination` on purpose, because the two are separate
 * facts. "What this entity is doing" changes when a walk starts, ends or is
 * replaced; "which click the server last answered" changes only when an intent
 * is applied — and an applied intent does not always start a walk. Clicking the
 * cell you are already standing in is answered, correctly, with no walk at all,
 * and a client left waiting for an acknowledgement of that click would conclude
 * its message was lost.
 *
 * The single writer of `lastSequence`. Only ever called for an intent the
 * server *applied*: silence is how a refused intent is reported.
 */
export const acknowledgeIntent = (player, sequence) => {
    player.lastSequence = sequence;
};
//# sourceMappingURL=state.js.map